/**
 * Grace Period Modal Component
 *
 * Dismissible modal that appears when a user with 2+ projects logs in during the grace period.
 * Informs the user they have free access to all projects for one week, after which
 * they will need to upgrade or select one project to keep.
 *
 * @module @ideoz/ui/components/molecules/GracePeriodModal
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeaderWithClose,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../atoms/dialog';
import { Button } from '../atoms/button';
import { cn } from '@ideoz/ui';

/**
 * Props for the GracePeriodModal component
 */
export interface GracePeriodModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;

  /**
   * Handler called when the modal is closed (X button or "Got it" button)
   */
  onClose: () => void;

  /**
   * Handler called when user clicks "View Plans"
   */
  onViewPlans: () => void;

  /**
   * Grace period end date (ISO 8601 format)
   */
  gracePeriodEndTime?: string;

  /**
   * Whether an action is processing (disables buttons)
   */
  isProcessing?: boolean;

  /**
   * Optional CSS class name
   */
  className?: string;

  /**
   * Test ID for testing purposes
   */
  testId?: string;
}

/**
 * GracePeriodModal Component
 *
 * Dismissible modal that notifies users with 2+ projects about the grace period.
 * Shows only once (first login after release) during the grace period.
 *
 * @component
 * @example
 * ```tsx
 * <GracePeriodModal
 *   isOpen={true}
 *   onClose={() => handleClose()}
 *   onUpgrade={() => handleUpgrade()}
 * />
 * ```
 */
export const GracePeriodModal: React.FC<GracePeriodModalProps> = ({
  isOpen,
  onClose,
  onViewPlans,
  gracePeriodEndTime,
  isProcessing = false,
  className = '',
  testId = 'grace-period-modal',
}) => {
  // Format the grace period end date
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      // Format as DD/MM/YYYY
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return '';
    }
  };

  const formattedEndDate = formatDate(gracePeriodEndTime);

  // Handle dialog open state change
  const handleOpenChange = (open: boolean) => {
    if (!open && !isProcessing) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn('sm:max-w-md', className)}
        data-testid={testId}
        onInteractOutside={(e) => {
          // Allow closing on backdrop click unless processing
          if (isProcessing) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          // Allow closing on Escape unless processing
          if (isProcessing) {
            e.preventDefault();
          }
        }}
      >
        {/* Header with close button */}
        <DialogHeaderWithClose>
          <DialogTitle>
            Grace Period Notice
          </DialogTitle>
        </DialogHeaderWithClose>

        {/* Description */}
        <DialogDescription className="text-base">
          You have free access to all your projects until the end of {formattedEndDate}. After that, you'll need to upgrade or select 1 project to continue working on.
        </DialogDescription>

        {/* Action Buttons */}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="secondary-gray"
            onClick={onClose}
            disabled={isProcessing}
            data-testid={`${testId}-got-it-button`}
          >
            OK, Got it
          </Button>
          <Button
            onClick={onViewPlans}
            disabled={isProcessing}
            data-testid={`${testId}-view-plans-button`}
          >
            View Plans
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

GracePeriodModal.displayName = 'GracePeriodModal';

export default GracePeriodModal;
